// NotificationBell.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../../firebase";
import {
  collection, query, orderBy, limit, onSnapshot, updateDoc, doc, writeBatch
} from "firebase/firestore";
import { Bell, X } from "lucide-react";

async function clearBadgeEverywhere() {
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.ready;
      reg?.active?.postMessage({ type: "CLEAR_BADGE" });
    }
    if (navigator.clearAppBadge) await navigator.clearAppBadge();
  } catch {}
}

export default function NotificationBell({ uid }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const unbindRef = useRef(null);

  const unreadCount = useMemo(() => items.filter(n => !n.read).length, [items]);

  // Live notifications list (Firestore)
  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, "users", uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(20)
    );
    const off = onSnapshot(q, (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => off();
  }, [uid]);

  // Foreground push → prepend (bind ONLY if eligible; lazy import)
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { canOfferPush } = await import("../../pushPolicy");
        const ok = await canOfferPush();
        if (!ok || !uid) return;
        const mod = await import("../../push"); // { bindForegroundPush }
        if (cancelled) return;
        unbindRef.current = mod.bindForegroundPush((payload) => {
          const d = (payload && payload.data) || {};
          setItems(prev => [
            {
              id: `temp-${Date.now()}`,
              title: d.title || "MikDok",
              body: d.body || "You have an update",
              url: d.url || "/",
              read: false,
              createdAt: { toDate: () => new Date() }
            },
            ...prev
          ]);
        });
      } catch (e) {
        // Ignore; just means no foreground binding on this browser
      }
    })();

    return () => {
      cancelled = true;
      if (typeof unbindRef.current === "function") unbindRef.current();
    };
  }, [uid]);

  // Desktop outside-click to close
  useEffect(() => {
    function onDocClick(e) {
      if (!isMobile && open && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, isMobile]);

  // Determine mobile (<= 640px)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const set = () => setIsMobile(mq.matches);
    set();
    mq.addEventListener?.("change", set);
    return () => mq.removeEventListener?.("change", set);
  }, []);

  // Prevent background scroll when sheet is open
  useEffect(() => {
    if (isMobile && open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [isMobile, open]);

  async function markAllRead() {
    if (!uid || unreadCount === 0) return;
    const batch = writeBatch(db);
    items.filter(n => !n.read).forEach((n) => {
      batch.update(doc(db, "users", uid, "notifications", n.id), { read: true });
    });
    await batch.commit();
    await clearBadgeEverywhere();
  }

  function goTo(n) {
    if (n?.id && !n.read) {
      updateDoc(doc(db, "users", uid, "notifications", n.id), { read: true }).catch(()=>{});
    }
    if (n?.url) window.location.href = n.url;
  }

  // (render unchanged)
  const bellBtnStyle = { position: "relative", width: 44, height: 44, padding: 0, color: "#6b7280",
    border: "none", background: "transparent", borderRadius: 10, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center" };
  const badgeStyle = { position: "absolute", top: 4, right: 4, minWidth: 18, height: 18, borderRadius: 9,
    backgroundColor: "#ef4444", color: "white", fontSize: 11, fontWeight: 700, display: "flex",
    alignItems: "center", justifyContent: "center", padding: "0 5px", lineHeight: 1 };

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Notifications"
        aria-label="Notifications"
        style={bellBtnStyle}
      >
        <Bell style={{ width: 22, height: 22 }} />
        {unreadCount > 0 && (
          <span style={badgeStyle}>{unreadCount > 99 ? "99+" : unreadCount}</span>
        )}
      </button>

      {/* Desktop dropdown */}
      {!isMobile && open && (
        <div style={{
          position: "absolute",
          right: 0,
          marginTop: 8,
          width: 360,
          maxHeight: 420,
          overflowY: "auto",
          background: "white",
          borderRadius: 12,
          border: "1px solid rgba(229,231,235,.8)",
          boxShadow: "0 10px 30px rgba(0,0,0,.1)",
          zIndex: 100
        }}>
          <Header unreadCount={unreadCount} markAllRead={markAllRead} />
          <List items={items} goTo={goTo} />
        </div>
      )}

      {/* Mobile sheet */}
      {isMobile && open && (
        <div
          role="dialog"
          aria-modal="true"
          style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,.35)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div style={{
            position: "absolute", top: 0, right: 0, bottom: 0, left: 0,
            background: "white", borderTopLeftRadius: 16, borderTopRightRadius: 16
          }}>
            <div style={{
              padding: "16px 16px calc(16px + env(safe-area-inset-top))",
              paddingTop: "calc(12px + env(safe-area-inset-top))",
              borderBottom: "1px solid #f3f4f6",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              position: "sticky", top: 0, background: "white", zIndex: 1
            }}>
              <strong style={{ color: "#111827", fontSize: 18 }}>Notifications</strong>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={markAllRead}
                  disabled={unreadCount === 0}
                  style={{
                    fontSize: 13, padding: "8px 12px", borderRadius: 10,
                    border: "1px solid #d1d5db", background: unreadCount ? "white" : "#f9fafb",
                    color: "#374151", cursor: unreadCount ? "pointer" : "default"
                  }}
                >
                  Mark all
                </button>
                <button
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                  style={{
                    width: 40, height: 40, borderRadius: 10, border: "1px solid #e5e7eb",
                    background: "white", display: "flex", alignItems: "center", justifyContent: "center"
                  }}
                >
                  <X style={{ width: 20, height: 20 }} />
                </button>
              </div>
            </div>

            <div style={{ height: "100%", overflowY: "auto", paddingBottom: "calc(16px + env(safe-area-inset-bottom))" }}>
              <List items={items} goTo={(n) => { setOpen(false); goTo(n); }} touchFriendly />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Header({ unreadCount, markAllRead }) {
  return (
    <div style={{
      padding: "12px 14px",
      borderBottom: "1px solid #f3f4f6",
      display: "flex", alignItems: "center", justifyContent: "space-between"
    }}>
      <strong style={{ color: "#111827" }}>Notifications</strong>
      <button
        onClick={markAllRead}
        disabled={unreadCount === 0}
        style={{
          fontSize: 12, padding: "6px 10px", borderRadius: 8,
          border: "1px solid #d1d5db", background: unreadCount ? "white" : "#f9fafb",
          color: "#374151", cursor: unreadCount ? "pointer" : "default"
        }}
      >
        Mark all read
      </button>
    </div>
  );
}

function List({ items, goTo, touchFriendly = false }) {
  if (!items || items.length === 0) {
    return <div style={{ padding: 16, color: "#6b7280", fontSize: 14 }}>You’re all caught up.</div>;
  }
  return items.map((n) => (
    <div
      key={n.id}
      onClick={() => goTo(n)}
      style={{
        padding: touchFriendly ? "16px 18px" : "12px 14px",
        borderBottom: "1px solid #f1f5f9",
        background: n.read ? "white" : "f8fafc",
        cursor: "pointer",
        minHeight: touchFriendly ? 64 : undefined,
        display: "flex", flexDirection: "column", justifyContent: "center"
      }}
    >
      <div style={{ fontSize: touchFriendly ? 16 : 14, fontWeight: 600, color: "#111827", marginBottom: 4 }}>
        {n.title || "Update"}
      </div>
      {n.body && (
        <div style={{ fontSize: touchFriendly ? 14 : 13, color: "#6b7280" }}>
          {n.body}
        </div>
      )}
    </div>
  ));
}
